
import React, { useState } from 'react';
import { UserProfile, Language } from '../types';
import { translations } from '../i18n';
import { GlassCard, Button } from '../components/shared';
import { useWonderEngine } from '../hooks/useWonderEngine';
import { Rocket, User, Star, Hash, Zap, Palette, Music, Book, Cpu, ArrowRight, Check } from 'lucide-react';

interface OnboardingWizardProps {
  user: UserProfile;
  lang: Language;
  onComplete: () => void;
}

const AVATARS = [
  { id: 'rocket', icon: '🚀', color: 'bg-blue-500' },
  { id: 'alien', icon: '👽', color: 'bg-green-500' },
  { id: 'robot', icon: '🤖', color: 'bg-slate-500' },
  { id: 'cat', icon: '🐱', color: 'bg-orange-500' },
  { id: 'dino', icon: '🦖', color: 'bg-emerald-600' },
  { id: 'star', icon: '⭐', color: 'bg-yellow-400' },
];

const INTEREST_TAGS = [
  { id: 'space', label: { en: 'Space', zh: '太空' }, icon: Rocket },
  { id: 'dinos', label: { en: 'Dinosaurs', zh: '恐龙' }, icon: Zap },
  { id: 'art', label: { en: 'Drawing', zh: '绘画' }, icon: Palette },
  { id: 'music', label: { en: 'Music', zh: '音乐' }, icon: Music },
  { id: 'coding', label: { en: 'Coding', zh: '编程' }, icon: Cpu },
  { id: 'reading', label: { en: 'Reading', zh: '阅读' }, icon: Book },
];

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ user, lang, onComplete }) => {
  const t = translations[lang];
  const { actions } = useWonderEngine(lang);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Form State
  const [avatar, setAvatar] = useState(user.avatar || 'rocket');
  const [grade, setGrade] = useState(user.grade || 1);
  const [age, setAge] = useState(user.age || 7);
  const [interests, setInterests] = useState<string[]>([]);

  const handleNext = async () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      setLoading(true);
      await actions.updateProfile({
        avatar,
        grade,
        age,
        interests
      });
      // Small delay for effect
      setTimeout(() => {
        setLoading(false);
        onComplete();
      }, 800);
    }
  };

  const toggleInterest = (id: string) => {
    if (interests.includes(id)) {
      setInterests(prev => prev.filter(i => i !== id));
    } else {
      if (interests.length < 5) {
        setInterests(prev => [...prev, id]);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      {/* Background Ambience */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
         <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[120px] animate-pulse-slow"></div>
         <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-[120px] animate-pulse-slow"></div>
      </div>

      <GlassCard variant="dark" className="w-full max-w-lg relative z-10 p-8 md:p-10 border-white/20 shadow-2xl shadow-indigo-900/50">
        
        {/* Progress Bar */}
        <div className="flex justify-between items-center mb-10">
          {[1, 2, 3].map((s) => (
             <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${step >= s ? 'bg-indigo-500 border-indigo-400 text-white shadow-[0_0_10px_rgba(99,102,241,0.5)]' : 'bg-slate-800 border-slate-700 text-slate-500'}`}>
                  {step > s ? <Check size={14}/> : s}
                </div>
                {s < 3 && <div className={`w-12 h-1 bg-slate-800 rounded-full ${step > s ? 'bg-indigo-500' : ''}`}></div>}
             </div>
          ))}
        </div>

        {/* Content Area */}
        <div className="min-h-[320px] animate-in slide-in-from-bottom-4 duration-500 key={step}">
          
          {/* STEP 1: Avatar */}
          {step === 1 && (
            <div className="text-center">
              <h2 className="text-2xl font-black text-white mb-2">{t.obStep1}</h2>
              <p className="text-slate-400 mb-8">{t.obStep1Desc}</p>
              
              <div className="relative mb-8 group inline-block">
                <div className={`w-32 h-32 rounded-full flex items-center justify-center text-7xl shadow-2xl transition-transform ${AVATARS.find(a => a.id === avatar)?.color || 'bg-slate-700'}`}>
                   {AVATARS.find(a => a.id === avatar)?.icon}
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-3 py-1 rounded-full font-bold border border-white/20 whitespace-nowrap">
                   {user.name}
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                {AVATARS.map(av => (
                  <button 
                    key={av.id}
                    onClick={() => setAvatar(av.id)}
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl transition-all border-2 ${avatar === av.id ? 'border-white scale-110 shadow-lg shadow-white/20' : 'border-transparent opacity-50 hover:opacity-100 hover:bg-white/5'}`}
                  >
                    {av.icon}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Grade & Age */}
          {step === 2 && (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-black text-white mb-2">{t.obStep2}</h2>
                <p className="text-slate-400">{t.obStep2Desc}</p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-bold text-indigo-300 uppercase tracking-wider mb-3">{t.grade}</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[1, 2, 3, 4, 5, 6].map(g => (
                      <button 
                        key={g}
                        onClick={() => setGrade(g)}
                        className={`py-3 rounded-xl border-2 font-bold transition-all ${grade === g ? 'bg-indigo-600 border-white text-white shadow-lg' : 'bg-slate-800/50 border-white/10 text-slate-400 hover:border-indigo-400'}`}
                      >
                        G{g}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                   <label className="block text-xs font-bold text-indigo-300 uppercase tracking-wider mb-3">{t.age}</label>
                   <div className="relative">
                      <Star className="absolute left-4 top-3.5 text-slate-500" size={18}/>
                      <input 
                        type="number"
                        value={age} 
                        onChange={(e) => setAge(Number(e.target.value))}
                        className="w-full bg-slate-950/50 border border-white/10 rounded-xl py-3 pl-12 pr-4 text-white font-bold focus:border-indigo-500 outline-none transition-all"
                      />
                   </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Interests */}
          {step === 3 && (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-black text-white mb-2">{t.obStep3}</h2>
                <p className="text-slate-400">{t.obStep3Desc}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                 {INTEREST_TAGS.map(tag => {
                   const isSelected = interests.includes(tag.id);
                   const Icon = tag.icon;
                   return (
                     <button
                       key={tag.id}
                       onClick={() => toggleInterest(tag.id)}
                       className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border-2 transition-all ${isSelected ? 'bg-indigo-600 border-white text-white shadow-lg' : 'bg-slate-900/40 border-white/10 text-slate-400 hover:border-white/30'}`}
                     >
                       <Icon size={24} className={isSelected ? 'text-white' : 'text-slate-500'} />
                       <span className="text-sm font-bold">{lang === 'zh' ? tag.label.zh : tag.label.en}</span>
                     </button>
                   )
                 })}
              </div>
              <p className="text-center text-xs text-slate-500 mt-4">{interests.length === 0 ? t.pickInterests : `${interests.length} selected`}</p>
            </div>
          )}

        </div>

        {/* Action Button */}
        <div className="mt-8 pt-6 border-t border-white/10">
           <Button 
             onClick={handleNext} 
             disabled={loading}
             className="w-full py-4 text-lg shadow-indigo-500/40"
           >
             {loading ? t.obComplete : step === 3 ? t.startJourney : t.next} <ArrowRight size={20} className={loading ? 'hidden' : ''}/>
           </Button>
        </div>

      </GlassCard>
    </div>
  );
};
